import type { UploadDraft, VideoRecord } from "./types";
import { browserIntakeIssue } from "./lib/intake";
import { withRetry } from "./lib/retry";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  const body = await response.json().catch(() => ({})) as { error?: string } & T;
  if (!response.ok) {
    const issue = new Error(body.error || `Request failed (${response.status}).`);
    Object.assign(issue, { status: response.status });
    throw issue;
  }
  return body;
}

export async function publicVideos(): Promise<VideoRecord[]> {
  return (await request<{ videos: VideoRecord[] }>("/api/videos")).videos;
}

export async function videoBySlug(slug: string): Promise<VideoRecord> {
  return (await request<{ video: VideoRecord }>(`/api/videos/${encodeURIComponent(slug)}`)).video;
}

export async function adminVideos(): Promise<VideoRecord[]> {
  return (await request<{ videos: VideoRecord[] }>("/api/admin/videos")).videos;
}

export async function login(password: string): Promise<void> {
  await request("/api/admin/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export async function logout(): Promise<void> {
  await request("/api/admin/logout", { method: "POST" });
}

export async function updateVideo(id: string, input: Partial<UploadDraft>): Promise<{ slug: string }> {
  return request(`/api/admin/videos/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export async function replaceVideoPoster(id: string, poster: File): Promise<void> {
  await request(`/api/admin/videos/${id}/poster`, {
    method: "PUT",
    headers: { "content-type": "image/jpeg" },
    body: poster,
  });
}

export async function replaceVideoCaptions(id: string, captions: File): Promise<void> {
  await request(`/api/admin/videos/${id}/captions`, {
    method: "PUT",
    headers: { "content-type": "text/vtt; charset=utf-8" },
    body: captions,
  });
}

export async function removeVideoCaptions(id: string): Promise<void> {
  await request(`/api/admin/videos/${id}/captions`, { method: "DELETE" });
}

export async function deleteVideo(id: string): Promise<void> {
  await request(`/api/admin/videos/${id}`, {
    method: "DELETE",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ confirmId: id }),
  });
}

interface UploadCallbacks {
  onProgress: (percent: number, message: string) => void;
  shouldCancel?: () => boolean;
  onStart?: (start: { id: string; uploadId: string }) => void;
}

class UploadCancelled extends Error {
  constructor() {
    super("Upload cancelled.");
  }
}

export async function finishUploadIfActive<T>(
  shouldCancel: (() => boolean) | undefined,
  finish: () => Promise<T>,
): Promise<T> {
  if (shouldCancel?.()) throw new UploadCancelled();
  return finish();
}

export interface AbortOutcome {
  record: boolean;
  multipart: boolean;
}

/**
 * Aborts an in-flight upload. Idempotent: an already-clean reply is only
 * ever sent by a server that removed the record after confirmed storage
 * cleanup. Any unknown or unconfirmed multipart state stays unconfirmed.
 * Retries transient failures a bounded number of times.
 */
export async function abortUpload(id: string, uploadId: string, fetchImpl: typeof fetch = fetch): Promise<AbortOutcome> {
  const attempt = async (): Promise<AbortOutcome> => {
    try {
      const response = await fetchImpl(`/api/admin/uploads/${id}?uploadId=${encodeURIComponent(uploadId)}`, { method: "DELETE" });
      if (!response.ok) return { record: false, multipart: false };
      const body = await response.json().catch(() => ({})) as { record?: string; multipart?: boolean | string };
      const multipartConfirmed = body.multipart === true;
      if (body.record === "already-clean") return { record: true, multipart: multipartConfirmed };
      if (body.record === "removed") return { record: true, multipart: multipartConfirmed };
      return { record: false, multipart: false };
    } catch {
      return { record: false, multipart: false };
    }
  };
  return withRetry(attempt, (outcome) => outcome.record && outcome.multipart);
}

async function loadVideoMetadata(file: File): Promise<{ duration: number; poster: Blob | null }> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let settled = false;
    const finish = (poster: Blob | null) => {
      if (settled) return;
      settled = true;
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      URL.revokeObjectURL(url);
      resolve({ duration, poster });
    };
    const timer = window.setTimeout(() => finish(null), 12_000);
    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const target = Math.min(Math.max(video.duration * 0.2, 0.2), 2);
      video.currentTime = target;
    };
    video.onseeked = () => {
      window.clearTimeout(timer);
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1600;
      canvas.height = video.videoHeight || 900;
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => finish(blob), "image/jpeg", 0.86);
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    video.src = url;
  });
}

export async function uploadVideo(file: File, captions: File | null, draft: UploadDraft, callbacks: UploadCallbacks): Promise<{ slug: string }> {
  callbacks.onProgress(1, "Reading video details");
  const metadata = await loadVideoMetadata(file);
  const intakeIssue = browserIntakeIssue(metadata.duration);
  if (intakeIssue) throw new Error(intakeIssue);
  const start = await request<{ id: string; slug: string; uploadId: string; chunkSize: number }>("/api/admin/uploads", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      ...draft,
      filename: file.name,
      mimeType: file.type || "video/mp4",
      durationSeconds: metadata.duration,
    }),
  });
  callbacks.onStart?.(start);

  if (callbacks.shouldCancel?.()) throw new UploadCancelled();
    if (metadata.poster) {
      callbacks.onProgress(3, "Creating poster frame");
      await request(`/api/admin/uploads/${start.id}/poster`, {
        method: "PUT",
        headers: { "content-type": "image/jpeg" },
        body: metadata.poster,
      });
    }
    if (captions) {
      callbacks.onProgress(4, "Adding captions");
      await request(`/api/admin/uploads/${start.id}/captions`, {
        method: "PUT",
        headers: { "content-type": "text/vtt; charset=utf-8" },
        body: captions,
      });
    }

    const parts: Array<{ partNumber: number; etag: string }> = [];
    const totalParts = Math.ceil(file.size / start.chunkSize);
    for (let index = 0; index < totalParts; index += 1) {
      if (callbacks.shouldCancel?.()) throw new UploadCancelled();
      const partNumber = index + 1;
      const chunk = file.slice(index * start.chunkSize, Math.min(file.size, (index + 1) * start.chunkSize));
      const result = await request<{ partNumber: number; etag: string }>(
        `/api/admin/uploads/${start.id}/parts/${partNumber}?uploadId=${encodeURIComponent(start.uploadId)}`,
        { method: "PUT", headers: { "content-type": "application/octet-stream" }, body: chunk },
      );
      parts.push(result);
      callbacks.onProgress(5 + Math.round((partNumber / totalParts) * 90), `Uploading part ${partNumber} of ${totalParts}`);
    }
    callbacks.onProgress(97, "Finishing upload");
    const complete = await finishUploadIfActive(callbacks.shouldCancel, () => (
      request<{ slug: string }>(`/api/admin/uploads/${start.id}/complete`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ uploadId: start.uploadId, parts, sizeBytes: file.size }),
      })
    ));
    callbacks.onProgress(100, "Ready to share");
    return complete;
}


export function sendAnalytics(videoId: string, type: "play" | "watch" | "complete", seconds = 0): void {
  const body = JSON.stringify({ type, seconds });
  const url = `/api/videos/${videoId}/events`;
  if (type === "watch" && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    return;
  }
  void fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true });
}
